'use client'

import { type ReactNode } from 'react'
import { cn } from '@nwwp/ui-shadcn'
import { useClientMounted } from '../../hooks/use-client-mounted'
import { loadLeadMagnetQualification } from '../../utils/lead-magnet-qualification'

const DEFAULT_DISCLOSURE = (
  <>
    NerdWallet Wealth Partners, LLC (&quot;NWWP&quot;) is an SEC-registered investment adviser. Registration does not
    imply skill or training. All investments involve risk, including potential loss of principal.
  </>
)

type LeadMagnetAdCardProps = {
  eyebrow: string
  headline: string
  body: ReactNode
  /** Schedule CTA (e.g. LeadMagnetScheduleCTA from the app or package). */
  cta: ReactNode
  disclosure?: ReactNode
  className?: string
}

/**
 * Qualified lead-magnet CTA card. Renders nothing until
 * `loadLeadMagnetQualification()` is true (client-mounted).
 */
export function LeadMagnetAdCard({
  eyebrow,
  headline,
  body,
  cta,
  disclosure = DEFAULT_DISCLOSURE,
  className,
}: LeadMagnetAdCardProps) {
  const mounted = useClientMounted()
  const isQualified = mounted && loadLeadMagnetQualification()

  if (!isQualified) return null

  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-gradient-to-br from-bone-light to-bone-warm p-6 text-center',
        className
      )}
    >
      <span className="mb-3 inline-block rounded border border-border bg-card px-2 py-1 text-subtitle-sm font-bold text-neutral-dark uppercase">
        AD
      </span>
      <p className="mb-1 text-body-sm font-semibold text-green uppercase">{eyebrow}</p>
      <p className="mb-2 text-body-lg font-semibold text-gray-darker">{headline}</p>
      <div className="mx-auto mb-4 max-w-130 text-subtitle text-neutral-dark">{body}</div>
      {cta}
      <p className="mx-auto mt-4 max-w-130 border-t border-border pt-4 text-body-xs text-neutral-dark">{disclosure}</p>
    </div>
  )
}
